import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { botService, blindboxService, websocketService, bilibiliBindingService, giftScreenshotService } from '../services';
import BackButton from '../components/BackButton';
import { useFeedback } from '../components/FeedbackProvider';
import { applyBlindboxUpdate, normalizeBlindboxStats } from '../utils/blindboxStats';
import './BlindBoxStats.css';

const TIME_RANGE_TYPES = ['current', 'previous', 'broadcast', 'today', 'week', 'month', 'custom'];

const normalizeTimeRangeType = (type) => (
  TIME_RANGE_TYPES.includes(type) ? type : 'current'
);

const parseDateTimeLocal = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime() / 1000;
};

const toDateTimeLocal = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(Number(timestamp) * 1000);
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  return new Date(Number(timestamp) * 1000).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatBroadcastOption = (broadcast) => {
  if (!broadcast) return '';
  const start = formatTimestamp(broadcast.start_time);
  const end = broadcast.end_time ? formatTimestamp(broadcast.end_time) : '直播中';
  return `${start} - ${end}`;
};

const rangeUsesLiveEnd = (timeRangeType, customEndTime) => {
  return timeRangeType !== 'custom' || !customEndTime;
};

function BlindBoxStats() {
  const navigate = useNavigate();
  const { confirm, prompt, toast } = useFeedback();
  
  // 绑定状态检查
  const [accessStatus, setAccessStatus] = useState({ loading: true, hasAccess: false, reason: '' });
  
  // 房间相关
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 盲盒相关
  const [blindBoxList, setBlindBoxList] = useState([]);
  const [selectedBlindBox, setSelectedBlindBox] = useState(null);
  
  // Time range
  const [broadcasts, setBroadcasts] = useState([]);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [timeRangeType, setTimeRangeType] = useState('current');
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  
  // 统计数据
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  
  // OBS 显示选项
  const [obsTransparent, setObsTransparent] = useState(true);
  const [obsShowTitle, setObsShowTitle] = useState(true);
  const [obsSize, setObsSize] = useState('medium');
  const [obsShowTotalCount, setObsShowTotalCount] = useState(false);
  const [obsShowProfit, setObsShowProfit] = useState(false);
  
  // OBS 访问令牌
  const [accessToken, setAccessToken] = useState(null);
  
  // WebSocket 连接状态
  const [wsConnected, setWsConnected] = useState(false);
  const currentRoom = rooms.find(r => String(r.uid) === String(selectedRoom));
  // 检查绑定状态
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const result = await bilibiliBindingService.checkFeatureAccess();
        setAccessStatus({
          loading: false,
          hasAccess: result.hasAccess,
          reason: result.reason || '',
          code: result.code || ''
        });
      } catch (err) {
        console.error('检查访问权限失败:', err);
        if (err.response?.status === 403) {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: err.response.data?.message || '您没有权限使用此功能',
            code: err.response.data?.code || ''
          });
        } else {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: '检查权限失败，请稍后重试'
          });
        }
      }
    };
    checkAccess();
  }, []);
  
  // 加载房间列表
  useEffect(() => {
    const loadRooms = async () => {
      try {
        setLoading(true);
        const config = await botService.getBotConfig();
        if (config && config.rooms) {
          const enabledRooms = config.rooms.filter(r => r.enabled);
          setRooms(enabledRooms);
          if (enabledRooms.length > 0) {
            // 使用 uid（主播UID）作为选中值，与 user_rooms 表存储保持一致
            const firstRoom = enabledRooms[0];
            setSelectedRoom(firstRoom.uid);
          }
        }
      } catch (err) {
        console.error('加载房间列表失败:', err);
        setError('加载房间列表失败');
      } finally {
        setLoading(false);
      }
    };
    
    loadRooms();
  }, []);
  
  // 加载盲盒列表
  useEffect(() => {
    const loadBlindBoxList = async () => {
      try {
        const list = await blindboxService.getBlindBoxList();
        setBlindBoxList(list || []);
      } catch (err) {
        console.error('加载盲盒列表失败:', err);
      }
    };
    
    loadBlindBoxList();
  }, []);
  
  // 加载配置和 OBS token
  useEffect(() => {
    if (!selectedRoom) return;
    
    const loadConfig = async () => {
      try {
        const config = await blindboxService.getBlindBoxConfig(selectedRoom);
        setSelectedBlindBox(config.selected_blind_gift);
        setObsTransparent(config.display_transparent !== false);
        setObsShowTitle(config.display_show_title !== false);
        setObsSize(config.display_size || 'medium');
        setObsShowTotalCount(config.display_show_total_count === true);
        setObsShowProfit(config.display_show_profit === true);
        setTimeRangeType(normalizeTimeRangeType(config.stats_time_range_type));
        setCustomStartTime(toDateTimeLocal(config.stats_custom_start_time));
        setCustomEndTime(toDateTimeLocal(config.stats_custom_end_time));
        if (config.stats_broadcast_id) {
          setSelectedBroadcast({ id: config.stats_broadcast_id });
        }
        
        // 加载 access token
        try {
          const tokenData = await blindboxService.getAccessToken(selectedRoom);
          setAccessToken(tokenData.access_token);
        } catch (err) {
          console.error('加载访问令牌失败:', err);
          setAccessToken(null);
        }
      } catch (err) {
        console.error('加载配置失败:', err);
      }
    };
    
    loadConfig();
  }, [selectedRoom]);
  
  // 加载直播场次列表
  useEffect(() => {
    if (!selectedRoom) return;

    const loadBroadcasts = async () => {
      try {
        const data = await giftScreenshotService.getBroadcasts(selectedRoom, 20);
        const list = data || [];
        setBroadcasts(list);

        setSelectedBroadcast((current) => {
          if (!current?.id) return list[0] || null;
          return list.find(item => String(item.id) === String(current.id)) || list[0] || null;
        });
      } catch (err) {
        console.error('加载直播场次失败:', err);
        setBroadcasts([]);
        setSelectedBroadcast(null);
      }
    };

    loadBroadcasts();
  }, [selectedRoom]);

  useEffect(() => {
    if (!selectedBroadcast?.id || selectedBroadcast.start_time || broadcasts.length === 0) return;
    const fullBroadcast = broadcasts.find(item => String(item.id) === String(selectedBroadcast.id));
    if (fullBroadcast) {
      setSelectedBroadcast(fullBroadcast);
    }
  }, [broadcasts, selectedBroadcast]);

  const selectCurrentBroadcast = useCallback(() => {
    setSelectedBroadcast(broadcasts[0] || null);
    setTimeRangeType('current');
  }, [broadcasts]);

  const selectPreviousBroadcast = useCallback(() => {
    setSelectedBroadcast(broadcasts[1] || broadcasts[0] || null);
    setTimeRangeType('previous');
  }, [broadcasts]);

  const handleBroadcastSelect = useCallback((broadcastId) => {
    const broadcast = broadcasts.find(item => String(item.id) === String(broadcastId));
    setSelectedBroadcast(broadcast || null);
    if (broadcast) {
      setTimeRangeType('broadcast');
    }
  }, [broadcasts]);

  // 计算实际查询时间范围；指定场次始终为该场开始到现在
  useEffect(() => {
    const now = Date.now() / 1000;
    let nextStart = null;
    let nextEnd = now;

    if (timeRangeType === 'current') {
      nextStart = broadcasts[0]?.start_time || null;
    } else if (timeRangeType === 'previous') {
      nextStart = (broadcasts[1] || broadcasts[0])?.start_time || null;
    } else if (timeRangeType === 'broadcast') {
      nextStart = selectedBroadcast?.start_time || null;
    } else if (timeRangeType === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      nextStart = today.getTime() / 1000;
    } else if (timeRangeType === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      weekAgo.setHours(0, 0, 0, 0);
      nextStart = weekAgo.getTime() / 1000;
    } else if (timeRangeType === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      monthAgo.setHours(0, 0, 0, 0);
      nextStart = monthAgo.getTime() / 1000;
    } else if (timeRangeType === 'custom') {
      nextStart = parseDateTimeLocal(customStartTime);
      nextEnd = parseDateTimeLocal(customEndTime) || now;
    }

    if (!nextStart) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      nextStart = today.getTime() / 1000;
    }

    setStartTime(nextStart);
    setEndTime(nextEnd);
  }, [timeRangeType, broadcasts, selectedBroadcast, customStartTime, customEndTime]);

  const getEffectiveEndTime = useCallback(() => {
    return rangeUsesLiveEnd(timeRangeType, customEndTime) ? Date.now() / 1000 : endTime;
  }, [timeRangeType, customEndTime, endTime]);

  const loadStats = useCallback(async () => {
    if (!selectedRoom || !startTime || !endTime) return;

    try {
      setStatsLoading(true);
      const statsData = await blindboxService.getBlindBoxStats(
        selectedRoom,
        null,
        startTime,
        getEffectiveEndTime()
      );
      setStats(normalizeBlindboxStats(statsData || {}));
    } catch (err) {
      console.error('加载盲盒统计失败:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [selectedRoom, startTime, endTime, getEffectiveEndTime]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (!selectedRoom || !startTime || !endTime) return undefined;
    const timer = setInterval(loadStats, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [selectedRoom, startTime, endTime, loadStats]);
  
  // WebSocket 连接
  useEffect(() => {
    if (!selectedRoom) return;
    
    // 连接状态监听
    const handleConnected = () => setWsConnected(true);
    const handleDisconnected = () => setWsConnected(false);
    
    websocketService.on('connected', handleConnected);
    websocketService.on('disconnected', handleDisconnected);
    
    // 订阅房间
    websocketService.subscribeRoom(selectedRoom);
    
    // 检查当前连接状态（解决从其他页面进入时状态不正确的问题）
    setWsConnected(websocketService.isConnected());
    
    return () => {
      websocketService.off('connected', handleConnected);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.unsubscribeRoom(selectedRoom);
    };
  }, [selectedRoom]);
  
  // 监听盲盒更新事件
  useEffect(() => {
    const currentRoomIds = new Set([
      selectedRoom,
      currentRoom?.uid,
      currentRoom?.room_id,
      currentRoom?.anchor_info?.room_id
    ].filter(Boolean).map(String));

    const handleBlindboxUpdate = (data) => {
      const updateData = data.data;
      if (!currentRoomIds.has(String(updateData.room_id))) return;
      if (!rangeUsesLiveEnd(timeRangeType, customEndTime)) return;

      setStats(prevStats => applyBlindboxUpdate(prevStats, updateData));
    };

    const handleBlindboxCleared = (data) => {
      if (!currentRoomIds.has(String(data.data?.room_id))) return;
      setStats({});
    };
    
    websocketService.on('blindbox_update', handleBlindboxUpdate);
    websocketService.on('blindbox_cleared', handleBlindboxCleared);
    
    return () => {
      websocketService.off('blindbox_update', handleBlindboxUpdate);
      websocketService.off('blindbox_cleared', handleBlindboxCleared);
    };
  }, [selectedRoom, currentRoom, timeRangeType, customEndTime]);
  
  const saveConfig = useCallback(async (overrides = {}) => {
    if (!selectedRoom) return;

    try {
      await blindboxService.saveBlindBoxConfig(selectedRoom, {
        selected_blind_gift: overrides.selectedBlindBox ?? selectedBlindBox,
        auto_clear_on_live: false,
        display_transparent: overrides.transparent ?? obsTransparent,
        display_show_title: overrides.showTitle ?? obsShowTitle,
        display_size: overrides.size ?? obsSize,
        display_show_total_count: overrides.showTotalCount ?? obsShowTotalCount,
        display_show_profit: overrides.showProfit ?? obsShowProfit,
        stats_time_range_type: overrides.timeRangeType ?? timeRangeType,
        stats_broadcast_id: overrides.broadcastId ?? selectedBroadcast?.id ?? null,
        stats_custom_start_time: parseDateTimeLocal(overrides.customStartTime ?? customStartTime),
        stats_custom_end_time: parseDateTimeLocal(overrides.customEndTime ?? customEndTime)
      });
    } catch (err) {
      console.error('保存盲盒配置失败:', err);
    }
  }, [
    selectedRoom,
    selectedBlindBox,
    obsTransparent,
    obsShowTitle,
    obsSize,
    obsShowTotalCount,
    obsShowProfit,
    timeRangeType,
    selectedBroadcast,
    customStartTime,
    customEndTime
  ]);

  // 选择盲盒
  const handleSelectBlindBox = useCallback((boxName) => {
    setSelectedBlindBox(boxName);
    saveConfig({ selectedBlindBox: boxName });
  }, [saveConfig]);

  const handleTimeRangeTypeChange = useCallback((type) => {
    setTimeRangeType(type);
    saveConfig({ timeRangeType: type });
  }, [saveConfig]);

  const handleSelectCurrent = useCallback(() => {
    const broadcast = broadcasts[0] || null;
    selectCurrentBroadcast();
    saveConfig({ timeRangeType: 'current', broadcastId: broadcast?.id ?? null });
  }, [broadcasts, selectCurrentBroadcast, saveConfig]);

  const handleSelectPrevious = useCallback(() => {
    const broadcast = broadcasts[1] || broadcasts[0] || null;
    selectPreviousBroadcast();
    saveConfig({ timeRangeType: 'previous', broadcastId: broadcast?.id ?? null });
  }, [broadcasts, selectPreviousBroadcast, saveConfig]);

  const handleBroadcastChange = useCallback((broadcastId) => {
    const broadcast = broadcasts.find(item => String(item.id) === String(broadcastId));
    handleBroadcastSelect(broadcastId);
    saveConfig({ timeRangeType: 'broadcast', broadcastId: broadcast?.id ?? null });
  }, [broadcasts, handleBroadcastSelect, saveConfig]);

  const handleCustomStartChange = useCallback((value) => {
    setCustomStartTime(value);
    saveConfig({ timeRangeType: 'custom', customStartTime: value });
  }, [saveConfig]);

  const handleCustomEndChange = useCallback((value) => {
    setCustomEndTime(value);
    saveConfig({ timeRangeType: 'custom', customEndTime: value });
  }, [saveConfig]);

  // 保存显示选项
  const saveDisplayOptions = useCallback((options) => {
    saveConfig(options);
  }, [saveConfig]);

  // 处理显示选项变更
  const handleTransparentChange = useCallback((value) => {
    setObsTransparent(value);
    saveDisplayOptions({ transparent: value });
  }, [saveDisplayOptions]);

  const handleShowTitleChange = useCallback((value) => {
    setObsShowTitle(value);
    saveDisplayOptions({ showTitle: value });
  }, [saveDisplayOptions]);

  const handleSizeChange = useCallback((value) => {
    setObsSize(value);
    saveDisplayOptions({ size: value });
  }, [saveDisplayOptions]);

  const handleShowTotalCountChange = useCallback((value) => {
    setObsShowTotalCount(value);
    saveDisplayOptions({ showTotalCount: value });
  }, [saveDisplayOptions]);

  const handleShowProfitChange = useCallback((value) => {
    setObsShowProfit(value);
    saveDisplayOptions({ showProfit: value });
  }, [saveDisplayOptions]);
  // 复制 OBS 源地址（使用 access_token 混淆）
  const handleCopyOBSUrl = useCallback(() => {
    if (!accessToken) {
      toast('正在获取访问令牌，请稍后重试', { type: 'warning' });
      return;
    }
    
    const url = `${window.location.origin}/live/blindbox/${accessToken}`;
    
    navigator.clipboard.writeText(url).then(() => {
      toast('OBS 源地址已复制到剪贴板，请勿泄露给他人', { type: 'success' });
    }).catch(err => {
      console.error('复制失败:', err);
      prompt({
        title: '手动复制 OBS 源地址',
        message: '浏览器没有允许自动复制，请手动复制下面的地址。此链接已加密，请勿泄露给他人。',
        label: 'OBS 源地址',
        defaultValue: url,
        confirmText: '完成',
        cancelText: '关闭',
        multiline: true,
        readOnly: true
      });
    });
  }, [accessToken, prompt, toast]);

  // 重新生成 access_token
  const handleRegenerateToken = useCallback(async () => {
    if (!selectedRoom) return;
    
    const confirmed = await confirm({
      title: '重新生成访问令牌',
      message: '确定要重新生成访问令牌吗？',
      detail: '重新生成后，旧的 OBS 源地址将失效，需要重新配置 OBS 浏览器源。',
      confirmText: '重新生成',
      variant: 'danger'
    });
    if (!confirmed) return;
    
    try {
      const result = await blindboxService.regenerateAccessToken(selectedRoom);
      setAccessToken(result.access_token);
      toast('访问令牌已重新生成', { type: 'success' });
    } catch (err) {
      console.error('重新生成令牌失败:', err);
      toast('重新生成令牌失败', { type: 'error' });
    }
  }, [confirm, selectedRoom, toast]);

  // 获取当前选中盲盒的统计数据（适配新格式）
  const currentBoxData = selectedBlindBox ? (stats[selectedBlindBox] || { gifts: [] }) : { gifts: [] };
  const currentStats = currentBoxData.gifts || [];

  // 获取当前房间信息
  // 检查绑定状态 loading
  if (accessStatus.loading) {
    return (
      <div className="container">
        <div className="blindbox-stats-page">
          <p>检查权限...</p>
        </div>
      </div>
    );
  }
  
  // 没有访问权限，重定向到首页
  if (!accessStatus.hasAccess) {
    navigate('/');
    return null;
  }
  
  if (loading) {
    return (
      <div className="container">
        <div className="blindbox-stats-page">
          <p>加载中...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container">
        <div className="blindbox-stats-page">
          <BackButton />
          <p className="error">{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container">
      <div className="blindbox-stats-page">
        <BackButton />
        
        <div className="page-header">
          <h1 className="page-title">📦 盲盒统计</h1>
          <Link to="/admin/gift-screenshot" className="link-btn">
            📸 礼物截图生成
          </Link>
        </div>
        
        <div className="main-layout">
          {/* 左侧预览区域 */}
          <div className="left-panel">
            <div className="section preview-section">
              <h2 className="section-title">OBS 预览</h2>
              <div className="preview-container">
                {selectedRoom && (
                  <iframe
                    key={`${accessToken || selectedRoom}-${obsTransparent}-${obsShowTitle}-${obsSize}`}
                    src={`/live/blindbox/${accessToken || selectedRoom}?preview=1`}
                    title="OBS Preview"
                    className="preview-iframe"
                  />
                )}
              </div>
            </div>
            
            {/* 连接状态 */}
            <div className="connection-status">
              <span className={`status-dot ${wsConnected ? 'connected' : 'disconnected'}`}></span>
              {wsConnected ? '实时连接已建立' : '实时连接断开'}
            </div>
          </div>
          
          {/* 右侧配置区域 */}
          <div className="right-panel">
            {/* 房间选择 */}
            <div className="section">
              <label className="section-label">选择直播间</label>
              <select 
                className="room-select"
                value={selectedRoom || ''}
                onChange={(e) => setSelectedRoom(e.target.value)}
              >
                {rooms.map(room => {
                  const displayRoomId = room.anchor_info?.room_id || room.uid;
                  const roomName = room.anchor_info?.uname || `UID ${room.uid}`;
                  return (
                    <option key={room.uid} value={room.uid}>
                      {roomName} (房间号: {displayRoomId})
                    </option>
                  );
                })}
              </select>
            </div>
            
            {/* 盲盒选择 */}
            <div className="section">
              <label className="section-label">选择盲盒</label>
              <div className="blindbox-grid">
                {blindBoxList.map(box => (
                  <button
                    key={box.name}
                    className={`blindbox-btn ${selectedBlindBox === box.name ? 'selected' : ''}`}
                    onClick={() => handleSelectBlindBox(box.name)}
                  >
                    {box.name}
                  </button>
                ))}
                {blindBoxList.length === 0 && (
                  <p className="no-data">暂无盲盒数据，请先在直播间收到盲盒礼物</p>
                )}
              </div>
            </div>
            
            {/* 时间范围 */}
            <div className="section time-range-section">
              <label className="section-label">统计时间范围</label>
              <div className="time-range-buttons compact">
                <button
                  className={timeRangeType === 'current' ? 'active' : ''}
                  onClick={handleSelectCurrent}
                  disabled={broadcasts.length === 0}
                >
                  {broadcasts.length > 0 && broadcasts[0].end_time === null ? '本场' : '最近一场'}
                </button>
                <button
                  className={timeRangeType === 'previous' ? 'active' : ''}
                  onClick={handleSelectPrevious}
                  disabled={broadcasts.length < 2}
                >
                  上场
                </button>
                <button
                  className={timeRangeType === 'today' ? 'active' : ''}
                  onClick={() => handleTimeRangeTypeChange('today')}
                >
                  今天
                </button>
                <button
                  className={timeRangeType === 'week' ? 'active' : ''}
                  onClick={() => handleTimeRangeTypeChange('week')}
                >
                  近 7 天
                </button>
                <button
                  className={timeRangeType === 'month' ? 'active' : ''}
                  onClick={() => handleTimeRangeTypeChange('month')}
                >
                  近 30 天
                </button>
                <button
                  className={timeRangeType === 'custom' ? 'active' : ''}
                  onClick={() => handleTimeRangeTypeChange('custom')}
                >
                  自定义
                </button>
              </div>

              <div className="broadcast-setting">
                <label>直播场次</label>
                <select
                  className={timeRangeType === 'broadcast' ? 'active' : ''}
                  value={selectedBroadcast?.id || ''}
                  onChange={(e) => handleBroadcastChange(e.target.value)}
                  disabled={broadcasts.length === 0}
                >
                  {broadcasts.length === 0 ? (
                    <option value="">暂无直播场次</option>
                  ) : (
                    broadcasts.map(broadcast => (
                      <option key={broadcast.id} value={broadcast.id}>
                        {formatBroadcastOption(broadcast)}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {timeRangeType === 'custom' && (
                <div className="custom-time-row">
                  <label>
                    开始
                    <input
                      type="datetime-local"
                      value={customStartTime}
                      onChange={(e) => handleCustomStartChange(e.target.value)}
                    />
                  </label>
                  <label>
                    结束
                    <input
                      type="datetime-local"
                      value={customEndTime}
                      onChange={(e) => handleCustomEndChange(e.target.value)}
                    />
                  </label>
                </div>
              )}

              {startTime && endTime && (
                <div className="time-range-display-top">
                  {formatTimestamp(startTime)} ~ {formatTimestamp(getEffectiveEndTime())}
                </div>
              )}
            </div>
            
            {/* OBS 显示选项 */}
            <div className="section obs-options">
              <label className="section-label">OBS 显示选项（自动保存）</label>

              <div className="options-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsTransparent}
                    onChange={(e) => handleTransparentChange(e.target.checked)}
                  />
                  透明背景
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsShowTitle}
                    onChange={(e) => handleShowTitleChange(e.target.checked)}
                  />
                  显示标题
                </label>

                <div className="size-selector">
                  <span>尺寸：</span>
                  <select
                    value={obsSize}
                    onChange={(e) => handleSizeChange(e.target.value)}
                    className="size-select"
                  >
                    <option value="small">小</option>
                    <option value="medium">中</option>
                    <option value="large">大</option>
                  </select>
                </div>
              </div>

              <div className="options-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsShowTotalCount}
                    onChange={(e) => handleShowTotalCountChange(e.target.checked)}
                  />
                  显示抽取总数
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsShowProfit}
                    onChange={(e) => handleShowProfitChange(e.target.checked)}
                  />
                  显示盈亏
                </label>
              </div>

              <div className="obs-url-actions">
                <button className="btn btn-secondary" onClick={handleCopyOBSUrl}>
                  复制 OBS 源地址
                </button>
                <button className="btn btn-outline" onClick={handleRegenerateToken} title="重新生成后旧链接将失效">
                  🔄 重新生成链接
                </button>
              </div>
              
              <p className="obs-url-hint">
                💡 OBS 链接已加密，不会直接暴露房间号
              </p>
            </div>
            
            {/* 统计展示 */}
            <div className="section">
              <h2 className="section-title">
                {selectedBlindBox ? `${selectedBlindBox} 礼物统计` : '请选择盲盒'}
              </h2>
              
              {statsLoading ? (
                <p>加载统计数据...</p>
              ) : selectedBlindBox ? (
                <div className="stats-list">
                  {currentStats.length > 0 ? (
                    currentStats.map((gift, index) => (
                      <div key={gift.gift_id || index} className="gift-item">
                        {gift.gift_img && (
                          <img 
                            src={gift.gift_img} 
                            alt={gift.gift_name} 
                            className="gift-icon"
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <span className="gift-name">{gift.gift_name}</span>
                        <span className="gift-price">{gift.gift_price / 100} 电池</span>
                        <span className="gift-count">x{gift.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">暂无统计数据</p>
                  )}
                </div>
              ) : (
                <p className="no-data">请先选择一个盲盒</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlindBoxStats;
